import client from "@/api/client"
import ConnectedHome from "@/components/ConnectedHome"
import { LandingPageListResponseDataItem } from "@/typings/api"
import { serializeSession } from "@/utils/session"
import { resolveWorkspaceHost } from "@/utils/workspaceHost"
import { captureException } from "@sentry/nextjs"
import { GetServerSidePropsContext } from "next"
import { unstable_getServerSession } from "next-auth"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import PublicHome from "../src/components/PublicHome"
import { authOptions } from "./api/auth/[...nextauth]"

export default function Home({
  isAuthenticated,
  landingData,
  marketing,
}: {
  isAuthenticated: boolean
  landingData: LandingPageListResponseDataItem
  marketing: boolean
}) {
  if (isAuthenticated && !marketing) {
    return <ConnectedHome />
  }

  return (
    <PublicHome
      landingData={landingData}
      marketing={marketing}
      isAuthenticated={isAuthenticated}
    />
  )
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const host = (context.req.headers.host || "").toLowerCase()
  const isMarketing =
    resolveWorkspaceHost(host, process.env.NEXT_PUBLIC_ROOT_DOMAIN as string)
      .kind === "marketing"

  let landingData = null
  const session = await unstable_getServerSession(
    context.req,
    context.res,
    authOptions
  )

  // Normalize the session for JSON serialization whenever there is one -
  // it is always returned in props below.
  if (session) {
    serializeSession(session)
  }

  // Fetch the landing content for anonymous visitors and for everyone on
  // the marketing root domain (signed-in visitors there see the public
  // site too).
  if (!session || isMarketing) {
    try {
      // The landing page is global public content, served without a
      // workspace (back workspace-resolver's WORKSPACE_AGNOSTIC_ROUTES).
      // Fetch it with the anonymous client - never the signed-in user's
      // JWT: Strapi's "authenticated" role has no `find` on
      // /api/landing-page (only the public role does), so a bearer token
      // here 403s and the whole landing section disappears for signed-in
      // visitors on the marketing root. Same pattern as pages/terms/*.
      const response = await client.landingPage.getLandingPage({
        // @ts-ignore
        "populate[presentation_1][populate][1]": "image",
        "populate[presentation_2][populate][1]": "image",
        "populate[presentation_3][populate][1]": "image",
        "populate[carousel][populate][1]": "image",
      })

      landingData = response.data.data
    } catch (e) {
      // eslint-disable-next-line
      console.error(e)
      captureException(e)
    }
  }

  return {
    props: {
      ...(await serverSideTranslations(context.locale!)),
      isAuthenticated: !!session?.userId,
      session,
      landingData,
      marketing: isMarketing,
    },
  }
}
